import supabaseClient from '@/api/supabaseClient.ts';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import type {
  GovernanceCheckCompassPreviewRequest,
  GovernanceCheckResponse,
} from '$/requests/GovernanceCheck.ts';
import { BackendErrorResponse } from '@/types/response/BackendErrorResponse';

/** Coalesce parallel COMPASS preview calls (e.g. React Strict Mode double-mount). */
const compassPreviewInFlight = new Map<string, Promise<GovernanceCheckResponse>>();

function compassPreviewCacheKey(body: GovernanceCheckCompassPreviewRequest): string {
  return [
    body.decision_id,
    body.commitment_confidence,
    body.primary_assumption.trim(),
    body.framework_logic_match,
  ].join(':');
}

export async function fetchGovernanceCompassPreview(
  body: GovernanceCheckCompassPreviewRequest,
): Promise<GovernanceCheckResponse> {
  const key = compassPreviewCacheKey(body);
  const existing = compassPreviewInFlight.get(key);
  if (existing) return existing;

  const promise = (async () => {
    const { data, error } = await supabaseClient.functions.invoke('governance-compass-preview', {
      body,
    });

    if (error) {
      if (error instanceof FunctionsHttpError) {
        const errorResponse: BackendErrorResponse = await error.context.json();
        throw new Error(errorResponse.errors?.join(', ') ?? 'COMPASS preview failed');
      }
      throw new Error(error.message || 'COMPASS preview failed');
    }

    const payload = data as { data?: GovernanceCheckResponse; errors?: string[] };
    if (payload?.errors?.length) {
      throw new Error(payload.errors.join(', '));
    }

    if (!payload?.data) {
      throw new Error('COMPASS preview failed');
    }

    return payload.data;
  })();

  compassPreviewInFlight.set(key, promise);
  try {
    return await promise;
  } finally {
    if (compassPreviewInFlight.get(key) === promise) {
      compassPreviewInFlight.delete(key);
    }
  }
}

type UseGovernanceCompassPreviewOptions = {
  enabled?: boolean;
};

const useGovernanceCompassPreview = (
  body: GovernanceCheckCompassPreviewRequest | null,
  options: UseGovernanceCompassPreviewOptions = {},
) => {
  const enabled = Boolean(body) && options.enabled !== false;

  return useQuery({
    queryKey: body
      ? [
          'governance-compass-preview',
          body.decision_id,
          body.commitment_confidence,
          body.primary_assumption,
          body.framework_logic_match,
        ]
      : ['governance-compass-preview', 'disabled'],
    queryFn: () => fetchGovernanceCompassPreview(body!),
    enabled,
    staleTime: Infinity,
    retry: 1,
  });
};

export default useGovernanceCompassPreview;
